// ✅ src/pages/VehicleV2/VehicleDailyProgressBulkInsert.jsx  (UPDATED FULL FILE)
// Backend expected:
//   app.use("/api/dailyProgressBulkInsert", dailyProgressBulkInsert);
//
// APIs used:
//   GET  https://nissi-india.com/api/dailyProgressBulkInsert/init
//   GET  https://nissi-india.com/api/dailyProgressBulkInsert/download-template?branchId=&date=&type=
//   POST https://nissi-india.com/api/dailyProgressBulkInsert/check
//   POST https://nissi-india.com/api/dailyProgressBulkInsert/save
//
// ✅ Updated pattern like DailyProgressListing / JobCardEntry / BatteryTesting:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - useMemo + useCallback pattern
// - loading + pageLoading + checking + saving states
// - safe init call
// - same UI card / header / button style
// - cleaner reset / submit / download / validation handling

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { FaSave, FaUndo, FaCheck, FaDownload } from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/dailyProgressBulkInsert";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const VehicleDailyProgressBulkInsert = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const fileRef = useRef(null);

  const [ms, setMs] = useState("0"); // microsetting: VehicleHSDCalculation
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    type: "Own",
    date: "",
    file: null,
  });

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white"
          : "bg-white border-gray-300 text-gray-900"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const isBusy = loading || checking || saving || pageLoading;

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        headers: commonHeaders,
      });

      if (data?.success) {
        setMs(toText(data?.ms ?? "0"));
      } else {
        setMs("0");
      }
    } catch (err) {
      console.error(err);
      setMs("0");
    } finally {
      setPageLoading(false);
    }
  }, [commonHeaders]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const handleChange = useCallback((e) => {
    const { name, value, files } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  }, []);

  const handleReset = useCallback(() => {
    setFormData({
      type: "Own",
      date: "",
      file: null,
    });

    if (fileRef.current) {
      fileRef.current.value = "";
    }

    toast.info("Form reset");
  }, []);

  const validateBeforeAction = useCallback(
    ({
      requireBranch = false,
      requireLogin = false,
      requireDate = false,
      requireFile = false,
    }) => {
      if (requireBranch && !clean(branchId)) {
        toast.error("BranchId not found in localStorage");
        return false;
      }

      if (requireLogin && !clean(loginUserId)) {
        toast.error("LoginUserId not found");
        return false;
      }

      if (requireDate && !clean(formData.date)) {
        toast.error("Please select Date");
        return false;
      }

      if (requireFile && !formData.file) {
        toast.error("Please Choose File");
        return false;
      }

      return true;
    },
    [branchId, loginUserId, formData.date, formData.file]
  );

  const handleDownload = useCallback(async () => {
    const ok = validateBeforeAction({
      requireBranch: true,
      requireDate: true,
    });
    if (!ok) return;

    try {
      setLoading(true);

      const { data, headers } = await axios.get(
        `${API_BASE}/download-template`,
        {
          params: {
            branchId: toInt(branchId, 0),
            date: clean(formData.date),
            type: clean(formData.type),
          },
          headers: commonHeaders,
          responseType: "blob",
        }
      );

      const blob = new Blob([data], {
        type: headers["content-type"] || "application/octet-stream",
      });

      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = "DPRBulkInsert.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);

      toast.success("Downloading format template...");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Download failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [validateBeforeAction, branchId, formData.date, formData.type, commonHeaders]);

  const handleCheck = useCallback(async () => {
    const ok = validateBeforeAction({
      requireDate: true,
      requireFile: true,
    });
    if (!ok) return;

    try {
      setChecking(true);

      const fd = new FormData();
      fd.append("date", clean(formData.date));
      fd.append("type", clean(formData.type));
      fd.append("file", formData.file);

      const { data } = await axios.post(`${API_BASE}/check`, fd, {
        headers: {
          ...commonHeaders,
          "Content-Type": "multipart/form-data",
        },
      });

      if (data?.success) {
        toast.success(data?.message || "Checked successfully");
        return;
      }

      const errs = Array.isArray(data?.errors) ? data.errors : [];
      if (errs.length > 0) {
        toast.error(errs.slice(0, 2).join(" | "));
      } else {
        toast.error(data?.message || "Validation failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Check failed");
    } finally {
      setChecking(false);
    }
  }, [validateBeforeAction, formData, commonHeaders]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const ok = validateBeforeAction({
        requireBranch: true,
        requireLogin: true,
        requireDate: true,
        requireFile: true,
      });
      if (!ok) return;

      try {
        setSaving(true);

        const fd = new FormData();
        fd.append("branchId", toInt(branchId, 0));
        fd.append("loginUserId", toInt(loginUserId, 0));
        fd.append("date", clean(formData.date));
        fd.append("type", clean(formData.type));
        fd.append("file", formData.file);

        const { data } = await axios.post(`${API_BASE}/save`, fd, {
          headers: {
            ...commonHeaders,
            "Content-Type": "multipart/form-data",
          },
        });

        if (!data?.success) {
          throw new Error(data?.message || "Save failed");
        }

        toast.success(data?.message || "Saved Successfully");
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Save failed"
        );
      } finally {
        setSaving(false);
      }
    },
    [
      validateBeforeAction,
      branchId,
      loginUserId,
      formData,
      commonHeaders,
    ]
  );

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE DAILY PROGRESS BULK INSERT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Daily Progress Bulk Insert
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                Upload Excel
              </span>
            </div>

            <div className="flex items-center gap-3">
              {isBusy && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : checking
                    ? "Checking..."
                    : saving
                    ? "Saving..."
                    : loading
                    ? "Downloading..."
                    : "Processing..."}
                </span>
              )}
            </div>
          </div>

          {/* Microsetting */}
          <div
            className={`rounded-xl p-4 mb-6 border text-sm ${
              isDark
                ? "bg-gray-800 border-gray-700 text-gray-300"
                : "bg-gray-50 border-gray-200 text-gray-700"
            }`}
          >
            Microsetting (VehicleHSDCalculation):{" "}
            <span className="font-semibold">{ms}</span>
          </div>

          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 uppercase">
                Bulk Insert Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Type */}
                <div>
                  <label className={labelClass}>
                    Type <span className="text-red-500">*</span>
                  </label>

                  <div className="flex items-center gap-6 h-[42px]">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="type"
                        value="Own"
                        checked={formData.type === "Own"}
                        onChange={handleChange}
                        disabled={isBusy}
                      />
                      <span
                        className={
                          isDark ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        Own
                      </span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="type"
                        value="Hire"
                        checked={formData.type === "Hire"}
                        onChange={handleChange}
                        disabled={isBusy}
                      />
                      <span
                        className={
                          isDark ? "text-gray-300" : "text-gray-700"
                        }
                      >
                        Hire
                      </span>
                    </label>
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className={labelClass}>
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={isBusy}
                    required
                  />
                </div>

                {/* Download format */}
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Download Format <span className="text-red-500">*</span>
                  </label>

                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={isBusy}
                      className={`flex items-center justify-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        isBusy ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      <FaDownload />
                      {loading ? "Please wait..." : "Download Format"}
                    </button>

                    <p
                      className={`text-sm ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Download the Excel template to format your data correctly.
                    </p>
                  </div>
                </div>

                {/* Upload */}
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Upload File <span className="text-red-500">*</span>
                  </label>

                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      isDark
                        ? "border-gray-600 bg-gray-900/40"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      name="file"
                      accept=".xls,.xlsx"
                      onChange={handleChange}
                      className="hidden"
                      id="file-upload"
                      disabled={isBusy}
                      required
                    />

                    <label htmlFor="file-upload" className="cursor-pointer">
                      <div className="flex flex-col items-center justify-center">
                        <svg
                          className="w-12 h-12 mx-auto mb-3 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>

                        <p
                          className={`text-sm ${
                            isDark ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          <span className="font-medium text-blue-600 hover:text-blue-500">
                            Click to upload
                          </span>{" "}
                          or drag and drop
                        </p>

                        <p
                          className={`text-xs mt-1 ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Excel files only (.xls / .xlsx)
                        </p>
                      </div>
                    </label>
                  </div>

                  {formData.file && (
                    <p
                      className={`text-sm mt-3 ${
                        isDark ? "text-green-300" : "text-green-600"
                      }`}
                    >
                      Selected file: {toStr(formData.file?.name)}
                    </p>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={isBusy}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium border transition-all duration-200 ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-700"
                      : "border-gray-300 text-gray-800 hover:bg-gray-100"
                  } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <FaUndo />
                  Reset
                </button>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCheck}
                    disabled={isBusy}
                    className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaCheck />
                    {checking ? "Checking..." : "Check"}
                  </button>

                  <button
                    type="submit"
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSave />
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </form>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleDailyProgressBulkInsert;